import { useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AdminPanel from "@/pages/AdminPanel";
import AdminDashboard from "@/pages/AdminDashboard";
import UserDetailDashboard from "@/pages/UserDetailDashboard";
import SupportClients from "@/pages/SupportClients";
import Reports from "@/pages/Reports";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import Settings from "@/pages/Settings";
import ResetPassword from "@/pages/ResetPassword";
import TicketTracking from "@/pages/TicketTracking";
import ClickUpCallback from "@/pages/ClickUpCallback";
import MaintenanceSchedule from "@/pages/MaintenanceSchedule";
import ServerDetail from "@/pages/ServerDetail";
import AppSidebar from "@/components/AppSidebar";
import { Toaster } from "@/components/ui/sonner";

console.log("API Base URL:", process.env.REACT_APP_API_BASE_URL);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("⚠️ REACT_APP_API_BASE_URL not found in environment variables. Defaulting to http://localhost:8000");
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && window.location.pathname !== '/login') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
          window.location.assign('/login');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {isAuthenticated && <AppSidebar user={user} onLogout={handleLogout} />}
        <div className={isAuthenticated ? "min-h-screen pt-14 lg:pt-0 lg:pl-64" : ""}>
          <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" /> :
                <Login onLogin={handleLogin} />
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/track/:token" element={<TicketTracking />} />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ?
                <Dashboard user={user} onLogout={handleLogout} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/admin"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'subadmin') ?
                <AdminDashboard user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/reports"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'subadmin') ?
                <Reports user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/users"
            element={
              isAuthenticated && user?.role === 'admin' ?
                <AdminPanel user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'subadmin') ?
                <UserDetailDashboard user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/support-clients"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'subadmin') ?
                <SupportClients user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/companies"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'subadmin') ?
                <Companies user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/companies/:companyId"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'subadmin') ?
                <CompanyDetail user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/settings"
            element={
              isAuthenticated && user?.role === 'admin' ?
                <Settings user={user} onLogout={handleLogout} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/maintenance"
            element={
              isAuthenticated ?
                <MaintenanceSchedule user={user} onLogout={handleLogout} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/maintenance/servers/:serverName"
            element={
              isAuthenticated ?
                <ServerDetail user={user} /> :
                <Navigate to="/login" />
            }
          />
          <Route path="/auth/clickup/callback" element={<ClickUpCallback />} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </div>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
export { API_BASE_URL };
